import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";

export default function FirstLoginPage() {
  const { user, refreshMe, logout } = useAuth();
  const navigate = useNavigate();
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    if (next.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (!/[a-zA-Z]/.test(next) || !/[^a-zA-Z]/.test(next)) {
      toast.error("Password must include at least one letter and one number or symbol.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/first-login-password", { new_password: next });
      const fresh = await refreshMe();
      toast.success("Password set. Welcome to Revenue Cycle IQ.");
      if (fresh?.role === "admin" && !fresh?.organization_id) {
        navigate("/admin/users", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to set password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0A192F] via-[#0F2238] to-[#0A192F] p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ background: "rgba(20,184,166,0.16)", color: "#5EEAD4", border: "1px solid rgba(20,184,166,0.3)" }}>
            <ShieldCheck className="h-3 w-3" /> First-time setup
          </div>
          <h1 className="font-display text-3xl text-white mt-4 tracking-tight">Set your password</h1>
          <p className="text-sm text-slate-400 mt-2">
            Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}. For security, please choose a new password before continuing.
          </p>
        </div>
        <form
          onSubmit={onSubmit}
          className="surface-card p-7 space-y-5"
          data-testid="first-login-form"
        >
          <div>
            <Label htmlFor="next">New password</Label>
            <Input
              id="next"
              type="password"
              autoComplete="new-password"
              autoFocus
              required
              minLength={8}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="first-login-new-password"
              className="h-11 rounded-xl mt-1.5"
            />
            <p className="text-[11px] text-slate-500 mt-1.5">
              At least 8 characters, with one letter and one number or symbol.
            </p>
          </div>
          <div>
            <Label htmlFor="confirm">Confirm password</Label>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="first-login-confirm-password"
              className="h-11 rounded-xl mt-1.5"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="ai-cta rounded-xl px-5 h-11 w-full"
            data-testid="first-login-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
            Continue
          </Button>
          <button
            type="button"
            onClick={() => { logout(); navigate("/login"); }}
            className="text-xs text-slate-500 hover:text-slate-700 w-full"
            data-testid="first-login-cancel"
          >
            Cancel and sign out
          </button>
        </form>
      </div>
    </div>
  );
}
